<template>
    <div class="system-shell">
        <aside
            v-if="getIsSuperuser"
            :class="['system-sidebar !hidden md:!flex', isSidebarCollapsed ? 'is-collapsed' : '']"
        >
            <div class="system-sidebar-brand">
                <div class="system-sidebar-brand__mark">
                    <svg-icon name="settings" class="h-5 w-5 fill-current" />
                </div>
                <div v-if="!isSidebarCollapsed" class="min-w-0">
                    <div class="system-sidebar-brand__title">{{ $tt('nav.system') }}</div>
                    <div class="system-sidebar-brand__sub">{{ getUsername }}</div>
                </div>
            </div>

            <ul class="system-sidebar-menu">
                <li v-for="item in menuItems" :key="item.index">
                    <button
                        type="button"
                        :class="['system-nav-item', { active: activeIndex === item.index }]"
                        :title="isSidebarCollapsed ? item.label : ''"
                        @click="handleActive(item.index)"
                    >
                        <svg-icon :name="item.icon" class="h-5 w-5 shrink-0 fill-current" />
                        <span v-if="!isSidebarCollapsed" class="truncate">{{ item.label }}</span>
                    </button>
                </li>
            </ul>

            <button class="system-sidebar-toggle" type="button" @click="toggleSidebar">
                <span :class="['system-sidebar-toggle__icon', isSidebarCollapsed ? 'is-reversed' : '']">
                    <svg-icon name="reverse" class="h-4 w-4 fill-current" />
                </span>
                <span v-if="!isSidebarCollapsed" class="truncate">{{ $tt('收起') }}</span>
            </button>
        </aside>

        <main class="system-main">
            <header class="system-header">
                <div class="min-w-0">
                    <div class="system-header__kicker">{{ $tt('nav.system') }}</div>
                    <h1 class="system-header__title">{{ currentMenuItem.label }}</h1>
                    <p class="system-header__summary">{{ currentMenuItem.summary }}</p>
                </div>
                <div class="system-header__meta">
                    <span class="system-header__role">
                        {{ getIsSuperuser ? $tt('管理员') : $tt('个人配置') }}
                    </span>
                </div>
            </header>

            <nav v-if="getIsSuperuser" class="system-mobile-nav md:hidden" aria-label="System settings">
                <button
                    v-for="item in menuItems"
                    :key="item.index"
                    type="button"
                    :class="['system-mobile-nav__item', { active: activeIndex === item.index }]"
                    @click="handleActive(item.index)"
                >
                    <svg-icon :name="item.icon" class="h-4 w-4 shrink-0 fill-current" />
                    <span>{{ item.label }}</span>
                </button>
            </nav>

            <section class="system-content">
                <component
                    :is="currentComponent"
                    :form-data="formData"
                    @update-sys="HandleUpdateSys"
                    @redirect-edit="redirctEditDetail"
                />
            </section>
        </main>
    </div>
</template>
<script>
import BasicSettings from './components/BasicSettings.vue'
import BackgroundScraping from './components/BackgroundScraping.vue'
import BackgroundTasks from './components/BackgroundTasks.vue'
import XiaoaiSpeaker from './components/XiaoaiSpeaker.vue'
import WebdavMusic from './components/WebdavMusic.vue'
import SystemInfo from './components/SystemInfo.vue'
import OpenApi from './components/OpenApi.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import RolePermission from './components/RolePermission.vue'
import PersonalizationSettings from './components/PersonalizationSettings.vue'
import { mapState, mapGetters } from 'vuex'

export default {
    components: {
        BasicSettings,
        BackgroundScraping,
        BackgroundTasks,
        XiaoaiSpeaker,
        WebdavMusic,
        SystemInfo,
        OpenApi,
        SvgIcon,
        RolePermission,
        PersonalizationSettings
    },
    data() {
        return {
            activeIndex: 1,
            formData: {'access_token': ''},
            isSidebarCollapsed: localStorage.getItem('system_sidebar_collapsed') === '1'
        }
    },
    computed: {
        ...mapGetters(['getUsername', 'getIsSuperuser']),
        ...mapState({
            homeAction: state => state.common.homeAction,
            homeActionTimestamp: state => state.common.homeActionTimestamp
        }),
        menuItems() {
            const items = [
                { index: 1, icon: 'sys-home', label: this.$tt('system.menu.basic'), summary: this.$tt('管理标签写入、元数据版本、Subsonic 与 Last.fm 等常用配置。') },
                { index: 2, icon: 'sys-backend', label: this.$tt('system.menu.scraping'), summary: this.$tt('配置后台刮削流程、目录监听、预处理和自动化执行规则。') },
                { index: 3, icon: 'sys-backend-task', label: this.$tt('system.menu.tasks'), summary: this.$tt('查看和控制后台任务，处理定时任务、删除任务与一次性维护操作。') },
                { index: 4, icon: 'sys-xiaoai', label: this.$tt('system.menu.xiaoai'), summary: this.$tt('连接小爱音响设备，配置播放、测试和设备发现。') },
                { index: 5, icon: 'sys-webdav', label: this.$tt('system.menu.webdav'), summary: this.$tt('管理 WebDAV 和 OpenList 音乐源的访问方式、缓存与认证信息。') },
                { index: 7, icon: 'sys-openapi', label: this.$tt('system.menu.openapi'), summary: this.$tt('维护开放 API、访问令牌、AI 服务和个性化背景配置。') },
                { index: 8, icon: 'user', label: this.$tt('system.menu.permission'), summary: this.$tt('查看系统用户并管理收藏家与听众角色。') },
                { index: 9, icon: 'settings', label: this.$tt('system.menu.personalization'), summary: this.$tt('设置语言、主题、站点标题和背景显示。') },
                { index: 6, icon: 'sys-info', label: this.$tt('system.menu.info'), summary: this.$tt('查看系统版本、运行环境、服务进程和 Worker 监控。') }
            ]
            return this.getIsSuperuser ? items : items.filter(item => item.index === 1)
        },
        currentMenuItem() {
            return this.menuItems.find(item => item.index === this.activeIndex) || this.menuItems[0]
        },
        currentComponent() {
            const componentMap = {
                1: 'BasicSettings',
                9: 'PersonalizationSettings',
                2: 'BackgroundScraping',
                3: 'BackgroundTasks',
                4: 'XiaoaiSpeaker',
                5: 'WebdavMusic',
                6: 'SystemInfo',
                7: 'OpenApi',
                8: 'RolePermission'
            }
            return componentMap[this.activeIndex] || 'BasicSettings'
        }
    },
    created() {
        this.fetchSysDict()
    },
    methods: {
        toggleSidebar() {
            this.isSidebarCollapsed = !this.isSidebarCollapsed
            localStorage.setItem('system_sidebar_collapsed', this.isSidebarCollapsed ? '1' : '0')
        },
        handleActive(index) {
            this.activeIndex = index
        },
        fetchSysDict() {
            this.$api.Task.getSysDict().then((res) => {
                if (res.result) {
                    this.formData = res.data
                    this.$store.commit('setSysDict', res.data)
                }
            })
        },
        HandleUpdateSys(key) {
            let value = this.formData[key]
            if (value.constructor === Boolean) {
                value = value ? '1' : '0'
            }
            if (key === 'silence_modify_list' || key === 'silence_source_list') {
                value = value.join(',')
            } else if (key === 'dir_list') {
                value = JSON.stringify(value)
            } else if (key === 'device_list' || key === 'silence_music_info' || key === 'artist_splitter_rules') {
                value = JSON.stringify(value)
            } else if (key === 'backend_task') {
                value = JSON.stringify(value)
            } else if (key === 'mi_play_in_pl') {
                window.localStorage.setItem('mi_play_in_pl', value)
            }
            this.$api.Task.UpdateSys({ 'key': key, value: value }).then((res) => {
                const nextSysDict = { ...this.$store.state.common.sysDict, [key]: this.formData[key] }
                if (key === 'opencode_server_username' || key === 'opencode_server_password') {
                    nextSysDict.opencode_authorization = this.buildOpenCodeAuthorization(nextSysDict)
                }
                this.$store.commit('setSysDict', nextSysDict)
                this.$myMsg.notify({
                    title: this.$tt('system.update.successTitle'),
                    type: 'success',
                    time: 1000,
                })
                // 如果修改的是网站标题，触发全局刷新事件
                if (key === 'site_title') {
                    this.$root.$emit('refresh-site-title')
                }
                // 如果修改的是版本标识显示，触发全局刷新事件
                if (key === 'show_role_badge') {
                    this.$root.$emit('refresh-role-badge')
                }
                if (key === 'brand_mark_type' || key === 'brand_mark_text') {
                    this.$root.$emit('refresh-brand-mark')
                }
            })
        },
        buildOpenCodeAuthorization(sysDict) {
            const username = sysDict.opencode_server_username || ''
            const password = sysDict.opencode_server_password || ''
            if (!username && !password) {
                return ''
            }
            return 'Basic ' + window.btoa(unescape(encodeURIComponent(username + ':' + password)))
        },
        redirctEditDetail(path) {
            if (this.$route.name !== 'home') {
                this.$router.push({ name: 'home' })
            }
            this.$store.commit('setFullPath', path)
        },
        handleHomeAction(actionName) {
            const actionNameList = actionName.split(":")
            this.activeIndex = Number(actionNameList[1])
        }
    },
    watch: {
        homeActionTimestamp(newTimestamp, oldTimestamp) {
            // 当时间戳变化时，执行对应的操作
            console.log(newTimestamp, oldTimestamp, this.homeAction)
            if (newTimestamp > oldTimestamp && this.homeAction) {
                this.handleHomeAction(this.homeAction)
            }
        }
    },
}
</script>
<style scoped>
.system-shell {
    display: flex;
    height: var(--system-shell-height, calc(100dvh - 70px));
    min-height: 0;
    gap: 0.75rem;
    overflow: hidden;
    padding: 0.5rem;
    background:
        radial-gradient(circle at top left, color-mix(in oklch, oklch(var(--p)) 12%, transparent), transparent 24rem),
        oklch(var(--b2));
    color: oklch(var(--bc));
}

.system-main {
    min-width: 0;
    flex: 1;
    width: 100%;
    max-width: 1480px;
    overflow-y: auto;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    border-radius: 1rem;
    background: color-mix(in oklch, oklch(var(--b1)) 88%, transparent);
    box-shadow: 0 18px 44px color-mix(in oklch, oklch(var(--bc)) 8%, transparent);
}

.system-header {
    position: sticky;
    top: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.25rem 1rem;
    border-bottom: 1px solid color-mix(in oklch, oklch(var(--bc)) 8%, transparent);
    background: color-mix(in oklch, oklch(var(--b1)) 92%, transparent);
    backdrop-filter: blur(16px);
    z-index: 2;
}

.system-header__kicker {
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: color-mix(in oklch, oklch(var(--bc)) 52%, transparent);
}

.system-header__title {
    margin: 0;
    font-size: clamp(1.5rem, 1.15rem + 1vw, 2.25rem);
    line-height: 1.1;
    font-weight: 800;
    letter-spacing: 0;
}

.system-header__summary {
    max-width: 44rem;
    margin-top: 0.45rem;
    color: color-mix(in oklch, oklch(var(--bc)) 62%, transparent);
    font-size: 0.875rem;
    line-height: 1.55;
}

.system-header__meta {
    display: flex;
    align-items: center;
    min-height: 2.25rem;
}

.system-header__role {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 0.75rem;
    border: 1px solid color-mix(in oklch, oklch(var(--p)) 26%, transparent);
    background: color-mix(in oklch, oklch(var(--p)) 10%, oklch(var(--b1)));
    color: oklch(var(--bc));
    padding: 0.45rem 0.7rem;
    font-size: 0.75rem;
    font-weight: 700;
}

.system-content {
    padding: 1.25rem;
}

.system-sidebar {
    width: 14.5rem;
    flex: 0 0 14.5rem;
    flex-direction: column;
    overflow: hidden;
    padding: 0.75rem;
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    border-radius: 1rem;
    background: color-mix(in oklch, oklch(var(--b1)) 90%, transparent);
    box-shadow: 0 18px 44px color-mix(in oklch, oklch(var(--bc)) 7%, transparent);
    transition: width 0.18s ease, flex-basis 0.18s ease;
}

.system-sidebar-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 3rem;
    padding: 0.45rem 0.45rem 0.9rem;
    border-bottom: 1px solid color-mix(in oklch, oklch(var(--bc)) 8%, transparent);
}

.system-sidebar-brand__mark {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    flex: 0 0 2.25rem;
    border-radius: 0.75rem;
    background: oklch(var(--n));
    color: oklch(var(--nc));
}

.system-sidebar-brand__title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 800;
    line-height: 1.2;
}

.system-sidebar-brand__sub {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: color-mix(in oklch, oklch(var(--bc)) 55%, transparent);
    font-size: 0.75rem;
    line-height: 1.4;
}

.system-sidebar-menu {
    display: flex;
    flex: 1;
    width: 100%;
    min-height: 0;
    flex-direction: column;
    gap: 0.25rem;
    margin: 0;
    padding: 0.75rem 0 0;
    list-style: none;
}

.system-sidebar-menu li {
    width: 100%;
}

.system-sidebar.is-collapsed {
    width: 4.75rem;
    flex-basis: 4.75rem;
    align-items: center;
    gap: 0.25rem;
}

.system-nav-item {
    display: flex;
    width: 100%;
    min-height: 2.75rem;
    align-items: center;
    gap: 0.75rem;
    border: 0;
    border-radius: 0.75rem;
    background: transparent;
    color: color-mix(in oklch, oklch(var(--bc)) 68%, transparent);
    padding: 0 0.85rem;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 700;
    white-space: nowrap;
    transition: background-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
    cursor: pointer;
}

.system-nav-item:hover {
    background: color-mix(in oklch, oklch(var(--bc)) 7%, transparent);
    color: oklch(var(--bc));
}

.system-nav-item:active,
.system-sidebar-toggle:active,
.system-mobile-nav__item:active {
    transform: translateY(1px);
}

.system-nav-item.active {
    background: oklch(var(--n));
    color: oklch(var(--nc));
    box-shadow: 0 12px 30px color-mix(in oklch, oklch(var(--n)) 24%, transparent);
}

.system-sidebar.is-collapsed li {
    display: flex;
    width: 2.75rem;
    min-width: 2.75rem;
    align-items: center;
    justify-content: center;
}

.system-sidebar.is-collapsed .system-nav-item {
    display: flex !important;
    width: 2.75rem;
    min-width: 2.75rem;
    height: 2.75rem;
    min-height: 2.75rem;
    justify-content: center;
    align-items: center !important;
    gap: 0;
    line-height: 1;
    padding-left: 0;
    padding-right: 0;
}

.system-sidebar-toggle {
    margin-top: auto;
    display: flex;
    width: 100%;
    min-height: 2.75rem;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    border-radius: 0.75rem;
    background: color-mix(in oklch, oklch(var(--b2)) 68%, transparent);
    color: color-mix(in oklch, oklch(var(--bc)) 70%, transparent);
    padding: 0 0.85rem;
    font-size: 0.875rem;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
}

.system-sidebar-toggle:hover {
    background: color-mix(in oklch, oklch(var(--bc)) 7%, transparent);
    color: oklch(var(--bc));
}

.system-sidebar-toggle__icon {
    display: inline-flex;
    transition: transform 0.18s ease;
}

.system-sidebar-toggle__icon.is-reversed {
    transform: rotate(180deg);
}

.system-sidebar.is-collapsed .system-sidebar-toggle {
    width: 2.75rem;
    min-width: 2.75rem;
    height: 2.75rem;
    min-height: 2.75rem;
    justify-content: center;
    align-items: center;
    gap: 0;
    padding-left: 0;
    padding-right: 0;
}

.system-mobile-nav {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    gap: 0.5rem;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid color-mix(in oklch, oklch(var(--bc)) 8%, transparent);
    scrollbar-width: none;
}

.system-mobile-nav::-webkit-scrollbar {
    display: none;
}

.system-mobile-nav__item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 2.25rem;
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    border-radius: 0.75rem;
    background: color-mix(in oklch, oklch(var(--b2)) 58%, transparent);
    color: color-mix(in oklch, oklch(var(--bc)) 68%, transparent);
    padding: 0 0.75rem;
    white-space: nowrap;
    font-size: 0.8125rem;
    font-weight: 700;
}

.system-mobile-nav__item.active {
    border-color: color-mix(in oklch, oklch(var(--n)) 32%, transparent);
    background: oklch(var(--n));
    color: oklch(var(--nc));
}

::v-deep .space-y-6 > :not([hidden]) ~ :not([hidden]),
::v-deep .space-y-8 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 1rem;
}

::v-deep .card {
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent) !important;
    border-radius: 1rem !important;
    background: color-mix(in oklch, oklch(var(--b1)) 94%, transparent) !important;
    box-shadow: 0 12px 28px color-mix(in oklch, oklch(var(--bc)) 7%, transparent) !important;
}

::v-deep .card .card {
    background: color-mix(in oklch, oklch(var(--b2)) 64%, oklch(var(--b1))) !important;
    box-shadow: none !important;
}

::v-deep .card-body {
    padding: 1.25rem;
}

::v-deep .card-title {
    gap: 0.5rem;
    margin-bottom: 0.85rem;
    color: oklch(var(--bc));
    line-height: 1.25;
    letter-spacing: 0;
}

::v-deep h2.card-title svg,
::v-deep h3 svg,
::v-deep h4 svg {
    color: oklch(var(--p));
}

::v-deep .bg-base-200\/50,
::v-deep .bg-base-200\/40,
::v-deep .bg-base-200\/30 {
    background: color-mix(in oklch, oklch(var(--b2)) 72%, oklch(var(--b1))) !important;
}

::v-deep .rounded-lg,
::v-deep .rounded-xl,
::v-deep .modal-box {
    border-radius: 1rem !important;
}

::v-deep .input,
::v-deep .select,
::v-deep .textarea,
::v-deep .file-input {
    min-height: 2.625rem;
    border-color: color-mix(in oklch, oklch(var(--bc)) 15%, transparent);
    border-radius: 0.75rem;
    background-color: color-mix(in oklch, oklch(var(--b1)) 90%, transparent);
}

::v-deep .input:focus,
::v-deep .select:focus,
::v-deep .textarea:focus,
::v-deep .file-input:focus {
    outline: 2px solid color-mix(in oklch, oklch(var(--p)) 46%, transparent);
    outline-offset: 2px;
}

::v-deep .label {
    padding-left: 0;
    padding-right: 0;
}

::v-deep .label-text {
    color: color-mix(in oklch, oklch(var(--bc)) 86%, transparent);
}

::v-deep .label-text-alt,
::v-deep .text-base-content\/60,
::v-deep .text-base-content\/70 {
    color: color-mix(in oklch, oklch(var(--bc)) 62%, transparent) !important;
}

::v-deep .btn {
    border-radius: 0.75rem;
    font-weight: 700;
    letter-spacing: 0;
    transition: transform 0.16s ease, background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease;
}

::v-deep .btn:active {
    transform: translateY(1px);
}

::v-deep .btn-ghost {
    color: color-mix(in oklch, oklch(var(--bc)) 74%, transparent);
}

::v-deep .btn-outline:not(.btn-error):not(.btn-warning):not(.btn-success):not(.btn-info) {
    border-color: color-mix(in oklch, oklch(var(--bc)) 16%, transparent);
    color: oklch(var(--bc));
}

::v-deep .alert {
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    border-radius: 1rem;
    background: color-mix(in oklch, oklch(var(--p)) 8%, oklch(var(--b1)));
    color: color-mix(in oklch, oklch(var(--bc)) 78%, transparent);
}

::v-deep .badge {
    border-radius: 0.65rem;
    font-weight: 700;
}

::v-deep .stats,
::v-deep .stat {
    border-radius: 1rem;
    background: color-mix(in oklch, oklch(var(--b2)) 72%, oklch(var(--b1))) !important;
    box-shadow: none !important;
}

::v-deep .stat-value {
    max-width: 100%;
    overflow-wrap: anywhere;
    font-size: 1rem;
    line-height: 1.4;
}

::v-deep .table {
    border-collapse: separate;
    border-spacing: 0;
}

::v-deep .table thead tr {
    background: color-mix(in oklch, oklch(var(--b2)) 74%, oklch(var(--b1))) !important;
}

::v-deep .table th {
    color: color-mix(in oklch, oklch(var(--bc)) 64%, transparent);
    font-size: 0.75rem;
    font-weight: 800;
}

::v-deep .table tbody tr {
    background: transparent !important;
}

::v-deep .table tbody tr:hover {
    background: color-mix(in oklch, oklch(var(--p)) 7%, transparent) !important;
}

::v-deep .modal-box {
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    background: oklch(var(--b1));
    box-shadow: 0 28px 80px color-mix(in oklch, oklch(var(--bc)) 18%, transparent);
}

::v-deep .drawer-side > div {
    border-left: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
}

@media (max-width: 767px) {
    .system-shell {
        height: var(--system-shell-height, calc(100dvh - 60px));
        padding: 0;
    }

    .system-main {
        border-width: 0;
        border-radius: 0;
    }

    .system-header {
        position: static;
        padding: 1rem 1rem 0.85rem;
    }

    .system-header__meta {
        display: none;
    }

    .system-content {
        padding: 1rem;
    }

    ::v-deep .card-body {
        padding: 1rem;
    }

    ::v-deep .btn {
        min-height: 2.25rem;
    }
}
</style>
